import {
  AsyncPipe,
  FormStyle,
  getLocaleDayNames,
  NgFor,
  NgIf,
  NgTemplateOutlet,
  TranslationWidth,
} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { TagManagerService } from '@sc/feature-tag-manager';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  FilterDisplayStyle,
  Fitter,
  FitterOpeningPeriod,
  Itinerary,
  LocationDataGeocoded,
  WeatherEvent,
} from '@sc/data-access-models';
import {
  CTAQuerySetAction,
  DossierSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import {
  AppointmentsSubService,
  AppointmentSubscriptionBuilder,
} from '@sc/feature-appointments-subscription';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { GmapComponent } from '@sc/feature-map';
import {
  AppointmentCommonListComponent,
  AppointmentFiltersComponent,
  ButtonComponent,
  FitterCommentComponent,
  FitterInfosBoxComponent,
  IconComponent,
  RatingComponent,
} from '@sc/ui-components';
import { AppointmentUtils, dayjs, ScreenUtils } from '@sc/utils';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'sc-fitter-page',
  styleUrls: ['./fitter-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AppointmentSubscriptionBuilder],
  imports: [
    NgIf,
    NgTemplateOutlet,
    AppointmentFiltersComponent,
    AppointmentCommonListComponent,
    NgFor,
    ButtonComponent,
    IconComponent,
    FitterInfosBoxComponent,
    GmapComponent,
    RatingComponent,
    FitterCommentComponent,
    AsyncPipe,
    TranslateModule,
  ],
  template: `
    <div class="top" *ngIf="isLoadSuccess() && !isMobileResolution()">
      <div class="background">
        <div class="top-left-block">
          <ng-container *ngIf="!allowOnlyThisFitter() || isModificationMode()">
            <ng-container
              *ngTemplateOutlet="linkBack; context: { onGoBack: onGoBack }"
            ></ng-container>
          </ng-container>

          <div class="fitter-logo">
            <img
              [src]="getLogoUrl()"
              [alt]="(getLogoAlt() | async) || fitter?.brand"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content" *ngIf="isLoadSuccess()">
      <div class="left-section" *ngIf="!isMobileResolution()">
        <div class="inside-left-section">
          <sc-appointment-filters
            *ngIf="canDisplayAppointments$ | async"
            id="appointment-filters-container"
            [displayListMapToggle]="false"
            [displayStyle]="filterStyle"
            [displayTimeFilters]="displayTimeFilters"
            [displayGroupBy]="false"
            [displayFitterInterventionFilters]="
              fittersSB.isNotItinerant(fitter)
            "
            [displayOutsideInterventionFilters]="
              fittersSB.isNotItinerant(fitter) && canFilterOutsideIntervention()
            "
            [displayReplacementVehicleFilters]="
              fittersSB.isNotItinerant(fitter) && canFilterReplacementVehicle()
            "
            [configurationTimeFilters]="
              [null].concat(configurationService.getConfig().timeFilters)
            "
          ></sc-appointment-filters>

          <sc-appointment-common-list
            id="appointment-lists-container"
            [filters]="
              (dossier$ | async)?.searchCriteria?.filters?.interventionParams
            "
            [displayReplacementVehicle]="canFilterReplacementVehicle()"
            [canFilterOutsideIntervention]="canFilterOutsideIntervention()"
            groupBy="days"
            [selectedAppointment]="selectedAppointment"
            [displaySelectFitter]="false"
            [appointmentSubscription]="appointmentSub"
            [loading]="loading$ | async"
            [dossier]="dossier$ | async"
            (slotClickEvent)="onSlotClick($event)"
            (requestAppointment)="onRequestAppointment()"
            sc-focus-return-zone
          ></sc-appointment-common-list>

          <p
            class="sc-design-text-3"
            *ngIf="!(canDisplayAppointments$ | async)"
          >
            {{ getNoSlotDescription() | translate }}
          </p>

          <ul class="weather-event-list">
            <li
              *ngFor="let weatherEvent of concernedWeatherEvents$ | async"
              class="weather-event-item"
            >
              <span
                class="weather-event-item-title"
                [innerHTML]="weatherEvent.title"
              ></span>
              <span
                class="weather-event-item-description"
                [innerHTML]="weatherEvent.description"
              ></span>
            </li>
          </ul>

          <sc-button
            *ngIf="
              displayAppointmentRequest() && !(canDisplayAppointments$ | async)
            "
            class="send-appointment-request-button"
            role="button"
            (click)="onRequestAppointment()"
          >
            {{ getRequestAppointmentBtnText() | translate }}
          </sc-button>

          <div
            class="other-fitter-bloc"
            *ngIf="
              isModificationMode() &&
              !allowOnlyThisFitter() &&
              canCancelAndBook()
            "
          >
            <p class="sc-design-text-3">
              {{ 'Fitter.otherFitterBlocTitle' | translate }}
            </p>
            <div
              class="other-fitter-btn"
              tabindex="0"
              role="button"
              (click)="onClickChoseOtherFitterBtn()"
              (keydown.enter)="onClickChoseOtherFitterBtn()"
              (keydown.space)="onClickChoseOtherFitterBtn()"
            >
              <span class="other-fitter-btn-left-span">
                <span class="other-fitter-btn-garage"
                  ><sc-icon name="fitter"></sc-icon
                ></span>
                <span class="sc-design-text-3-bold other-fitter-btn-text">
                  {{ 'Fitter.otherFitterBtnGarage' | translate }}</span
                >
              </span>
              <span class="other-fitter-btn-arrow"
                ><sc-icon name="arrow_right"></sc-icon
              ></span>
            </div>
          </div>
        </div>
      </div>

      <div class="right-section">
        <div class="inside-right-section">
          <h1 id="fitter-page-title" class="page-title sc-design-title-h1">
            {{ 'Fitter.information' | translate }}
          </h1>

          <div
            *ngIf="!(canDisplayAppointments$ | async) && isMobileResolution()"
            class="mobile-request-btn"
          >
            <p class="sc-design-text-3">
              {{ getNoSlotDescription() | translate }}
            </p>

            <ul class="weather-event-list">
              <li
                *ngFor="let weatherEvent of concernedWeatherEvents$ | async"
                class="weather-event-item"
              >
                <span
                  class="weather-event-item-title"
                  [innerHTML]="weatherEvent.title"
                ></span>
                <span
                  class="weather-event-item-description"
                  [innerHTML]="weatherEvent.description"
                ></span>
              </li>
            </ul>

            <sc-button
              *ngIf="displayAppointmentRequest()"
              class="send-appointment-request-button"
              role="button"
              (click)="onRequestAppointment()"
            >
              {{ getRequestAppointmentBtnText() | translate }}
            </sc-button>
          </div>

          <sc-fitter-infos-box
            [fitter]="fitter"
            [itineraryDistanceInMeters]="itineraryDistanceInMeters"
            [itineraryDurationInSeconds]="itineraryDurationInSeconds"
          ></sc-fitter-infos-box>

          <div
            class="planning"
            *ngIf="
              !isMobileResolution() && fitter?.openingHours?.center?.length
            "
          >
            <h2 class="sc-design-title-h2 page-sub-title">
              {{ 'Fitter.openingHours' | translate }}
            </h2>
            <dl class="days-container">
              <div class="day" *ngFor="let dayKey of [1, 2, 3, 4, 5, 6, 0]">
                <dt class="day-name sc-design-text-4-bold">
                  {{ days[dayKey] }}
                </dt>
                <dd class="hours">
                  <span
                    class="time sc-design-text-3"
                    *ngFor="
                      let period of groupOpeningPeriodsByDay().get(dayKey)
                    "
                  >
                    {{
                      formatGoogleTime(period.open.time) +
                        ' - ' +
                        formatGoogleTime(period.close.time)
                    }}
                  </span>
                  <span
                    class="time sc-design-text-3"
                    *ngIf="!groupOpeningPeriodsByDay().get(dayKey)"
                  >
                    {{ 'FitterOpeningHours.closed' | translate }}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <button
            class="show-opening-periods-btn"
            type="button"
            (click)="onShowOpeningPeriodsClick()"
          >
            <sc-icon name="calendar_hour"></sc-icon>
            <span class="show-more-periods-label sc-design-text-3">{{
              'Fitter.seeOpeningHours' | translate
            }}</span>
          </button>

          <sc-gmap
            *ngIf="shouldShowMap()"
            [defaultZoomLevel]="16"
            [defaultLatitude]="fitter?.location?.lat"
            [defaultLongitude]="fitter?.location?.lon"
            currentMapType="DISPLAY_FITTER"
            (fitterTrajectEvent)="onFitterItineraryButtonClick()"
          ></sc-gmap>

          <sc-appointment-filters
            *ngIf="isMobileResolution() && (canDisplayAppointments$ | async)"
            id="appointment-filters-container"
            [displayListMapToggle]="false"
            [displayStyle]="filterStyle"
            [displayTimeFilters]="displayTimeFilters"
            [displayGroupBy]="false"
            [displayFitterInterventionFilters]="
              fittersSB.isNotItinerant(fitter)
            "
            [displayOutsideInterventionFilters]="
              fittersSB.isNotItinerant(fitter) && canFilterOutsideIntervention()
            "
            [displayReplacementVehicleFilters]="
              fittersSB.isNotItinerant(fitter) && canFilterReplacementVehicle()
            "
            [displayGeneralistsFilter]="false"
            [configurationTimeFilters]="
              [null].concat(configurationService.getConfig().timeFilters)
            "
          ></sc-appointment-filters>

          <sc-appointment-common-list
            *ngIf="isMobileResolution() && (canDisplayAppointments$ | async)"
            id="appointment-lists-container"
            [filters]="
              (dossier$ | async)?.searchCriteria?.filters?.interventionParams
            "
            [displayReplacementVehicle]="canFilterReplacementVehicle()"
            [canFilterOutsideIntervention]="canFilterOutsideIntervention()"
            groupBy="days"
            [selectedAppointment]="selectedAppointment"
            [displaySelectFitter]="false"
            [appointmentSubscription]="appointmentSub"
            [loading]="loading$ | async"
            [dossier]="dossier$ | async"
            (slotClickEvent)="onSlotClick($event)"
            (requestAppointment)="onRequestAppointment()"
          ></sc-appointment-common-list>

          <div
            *ngIf="shouldDisplayComments()"
            id="fitter-rating-container"
            class="comments-header"
          >
            <div
              class="sc-design-title-h2 section-title"
              role="heading"
              aria-level="2"
            >
              {{ 'Fitter.customerReviews' | translate }}
            </div>
            <sc-rating
              [rating]="fitter?.google?.rating"
              [showNumberRating]="true"
              [numberRating]="fitter?.google?.review?.length"
            >
            </sc-rating>
          </div>

          <div *ngIf="shouldDisplayComments()" class="fitter-comments">
            <div class="fitter-comments-wrapper">
              <sc-fitter-comment
                class="fitter-comment"
                *ngFor="let review of fitter.google?.review?.slice(0, 3)"
                [review]="review"
                [compact]="isMobileResolution()"
                [sliceText]="true"
              ></sc-fitter-comment>
            </div>
          </div>

          <div
            *ngIf="shouldDisplayComments()"
            class="sc-design-text-3-bold show-more-comments"
            tabindex="0"
            role="button"
            (click)="onShowMoreCommentsClick()"
            (keydown.enter)="onShowMoreCommentsClick()"
            (keydown.space)="onShowMoreCommentsClick()"
          >
            <sc-icon name="comments"></sc-icon>
            <span>{{ 'Fitter.seeAllComments' | translate }}</span>
          </div>

          <div
            *ngIf="
              isMobileResolution() &&
              isModificationMode() &&
              !allowOnlyThisFitter() &&
              canCancelAndBook()
            "
            class="other-fitter-btn"
            tabindex="0"
            role="button"
            (click)="onClickChoseOtherFitterBtn()"
            (keydown.enter)="onClickChoseOtherFitterBtn()"
            (keydown.space)="onClickChoseOtherFitterBtn()"
          >
            <span class="other-fitter-btn-left-span">
              <span class="other-fitter-btn-garage"
                ><sc-icon name="fitter"></sc-icon
              ></span>
              <span class="sc-design-text-3-bold other-fitter-btn-text">
                {{ 'Fitter.otherFitterBtnGarage' | translate }}</span
              >
            </span>
            <span class="other-fitter-btn-arrow"
              ><sc-icon name="arrow_right"></sc-icon
            ></span>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class FitterPageComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild('linkBack') linkBack: TemplateRef<any>;

  @Input() filterStyle: FilterDisplayStyle = 'with-background';
  @Input() fitter?: Fitter;
  @Input() displayTimeFilters = true;
  @Input() displayComments = true;
  @Input() loading = false;
  @Input() useLastFilters = true;

  @Output() clickReturn = new EventEmitter<boolean>();
  @Output() clickChoseOtherFitter = new EventEmitter<void>();
  @Output() showOpeningsRedirect = new EventEmitter<void>();
  @Output() showCommentsRedirect = new EventEmitter<void>();
  @Output() updateItinerary = new EventEmitter<{
    meters: number;
    seconds: number;
  }>();
  @Output() book = new EventEmitter<{ dossier: Dossier; modified: boolean }>();
  @Output() requestAppointment = new EventEmitter<{
    dossier: Dossier;
  }>();

  days: readonly string[];

  queryAction?: string;

  searchLocation?: LocationDataGeocoded;

  dossier$: Observable<Dossier>;
  currentDossier: Dossier;

  loading$ = new BehaviorSubject<boolean>(true);

  itineraryDistanceInMeters = 0;
  itineraryDurationInSeconds = 0;

  selectedAppointment?: Appointment;

  appointmentSub: AppointmentsSubService;

  canDisplayAppointments$: Observable<boolean>;
  concernedWeatherEvents$: Observable<WeatherEvent[]>;

  isMobileResolution = ScreenUtils.isMobileResolution;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly assetsService: AssetsService,
    public configurationService: ConfigurationService,
    private readonly sandboxService: SandboxService,
    public fittersSB: FittersSandboxService,
    private readonly dossierSB: DossierSandboxService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translator: TranslatorService,
    private readonly overlayService: AdaptableOverlayService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly appointmentSubBuilder: AppointmentSubscriptionBuilder,
    private readonly tmService: TagManagerService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.translator.getCurrentLang().subscribe((newFormattedLang) => {
      this.days = getLocaleDayNames(
        newFormattedLang,
        FormStyle.Standalone,
        TranslationWidth.Wide
      );
    });

    this.dossier$ = this.dossierSB.dossier$;

    this.dossier$.subscribe((newDossier) => (this.currentDossier = newDossier));

    this.appointmentSub = this.appointmentSubBuilder
      .addInCenterObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams?.fitterIntervention
          )
        )
      )
      .addAtHomeObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams
                ?.outsideIntervention
          )
        )
      )
      .addReplacementVehicleObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams?.replacementVehicle
          )
        )
      )
      .addTimeFilterObservable(
        this.dossier$.pipe(map((d) => d?.searchCriteria?.filters?.period))
      )
      .addAppointmentsFilter((apmtList) =>
        apmtList.filter(
          (apmt) =>
            !this.featureFlippingService.isBrandFeatureActivated(
              this.fitter?.brandId,
              'diplay-other-fitters-PAD-slots'
            ) ||
            apmt.fitterId === this.fitter?.id ||
            (apmt.outsideIntervention && this.fitter?.brandId === apmt.brandId)
        )
      )
      .addFittersFilter((fitters) =>
        fitters.filter(
          (f) =>
            this.featureFlippingService.isBrandFeatureActivated(
              this.fitter?.brandId,
              'diplay-other-fitters-PAD-slots'
            ) || f.id === this.fitter?.id
        )
      )
      .checkCutOffFeature()
      .checkMobilityService()
      .checkReplacementVehicleService()
      .removeDuplicates()
      .build();

    this.canDisplayAppointments$ =
      this.appointmentSub.displayedNoAppointmentFitterCards$.pipe(
        map((fitters) => !fitters.some((f) => f.id === this.fitter?.id))
      );

    this.concernedWeatherEvents$ = this.fittersSB.weatherEvents$.pipe(
      map((weatherEvents) =>
        weatherEvents.filter((weatherEvent) =>
          weatherEvent?.fitters?.[this?.fitter?.brandId]?.includes(
            this.fitter.id
          )
        )
      )
    );

    this.route.queryParams.subscribe((queries) => {
      this.queryAction = queries?.action;
    });
  }

  ngOnInit(): void {
    this.appointmentSub.initSubscriptions();

    this.appointmentSub.terminalSearchStatus$.subscribe((s) => {
      this.loading$.next(false);
    });
  }

  shouldDisplayComments() {
    return (
      this.displayComments &&
      !this.featureFlippingService.isBrandFeatureActivated(
        this.fitter?.brandId,
        'no-display-google-reviews'
      )
    );
  }

  ngOnDestroy(): void {
    if (
      !this.featureFlippingService.isFeatureActivated(
        'preserve-selected-fitter-on-destroy'
      )
    ) {
      this.fittersSB.unselectFitter();
    }
    this.appointmentSub.destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes on fitter
    const previousFitter = changes['fitter']?.previousValue as Fitter;
    const currentFitter = changes['fitter']?.currentValue as Fitter;

    if (previousFitter?.id !== currentFitter?.id) {
      if (this.fittersSB.isNotItinerant(currentFitter)) {
        this.loadItineraryData();
      } else {
        this.dossierSB.patchDossierSearchFilters({
          interventionParams: { outsideIntervention: true },
        });
      }

      // Only show the overlay after the fitter has been loaded (it takes more time than to get the action query value)
      if (
        !previousFitter &&
        !!currentFitter &&
        this.queryAction === 'open-comments'
      ) {
        this.onShowMoreCommentsClick();
      }
    }
  }

  allowOnlyThisFitter() {
    return (
      this.dossierSB.isRebookOnlySameFitterActive(this.currentDossier) ||
      this.dossierSB.isModifyOnlySameFitterActive(this.currentDossier)
    );
  }

  canCancelAndBook() {
    return (
      this.featureFlippingService.isBrandFeatureActivated(
        this.currentDossier.appointment?.brandId,
        'can-do-appointment-cancel-fitter'
      ) &&
      this.configurationService?.getFitterBrandInfosByBrandId(
        this.currentDossier?.appointment?.brandId
      )?.booking?.modify?.strategy !== 'CANCEL_AND_BOOK'
    );
  }

  isModificationMode() {
    return (
      this.currentDossier?.appointment?.status === 'MODIFIED' ||
      this.currentDossier?.requestAppointment?.status === 'MODIFIED'
    );
  }

  isLoadSuccess() {
    return !this.loading && this.fitter;
  }

  shouldShowMap() {
    return (
      this.fittersSB.isNotItinerant(this.fitter) &&
      !this.featureFlippingService.isFeatureActivated('hide-fitter-map')
    );
  }

  canFilterOutsideIntervention() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'insurer-can-declare-in-mobility'
      ) &&
      this.featureFlippingService.isBrandFeatureActivated(
        this.fitter.brandId,
        'fitter-in-mobility-service'
      )
    );
  }

  canFilterReplacementVehicle() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'can-declare-replacement-vehicle-insurer'
      ) &&
      this.featureFlippingService.isBrandFeatureActivated(
        this.fitter.brandId,
        'can-declare-replacement-vehicle-fitter'
      ) &&
      this.currentDossier?.claim?.operationType !== 'Repair'
    );
  }

  getSearchLocationGeocoded(): LocationDataGeocoded {
    return this.selectedAppointment?.outsideIntervention
      ? this.currentDossier?.searchCriteria?.address ??
          ({
            addressElements: {
              postalCode: this.fitter?.cp,
              city: this.fitter?.city,
            },
          } as LocationDataGeocoded)
      : AppointmentUtils.getFitterLocation(this.fitter);
  }

  loadItineraryData(): void {
    if (
      this.currentDossier?.searchCriteria?.address?.placeId &&
      this.fitter?.location?.lat &&
      this.fitter?.location?.lon
    ) {
      const departureDateTimeInMillis = dayjs().unix();
      this.sandboxService
        .getItinerary(
          this.currentDossier?.searchCriteria.address.placeId,
          this.fitter?.location,
          departureDateTimeInMillis
        )
        .pipe(take(1))
        .subscribe((itinerary: Itinerary) => {
          if (itinerary) {
            this.itineraryDurationInSeconds = itinerary.duration;
            this.itineraryDistanceInMeters = itinerary.distance;
            this.changeDetectorRef.markForCheck();

            this.onChangeItinerary();
          }
        });
    }
  }

  getLogoUrl() {
    const brand = this.configurationService
      ?.getConfig()
      ?.fitterBrands?.find((brand) => brand?.id === this.fitter?.brandId);
    return this.assetsService?.getBrandLogoPath(brand?.logo, true);
  }

  getLogoAlt(): Observable<string> {
    return this.assetsService.getLogoAlt(
      this.configurationService
        ?.getConfig()
        ?.fitterBrands?.find((brand) => brand?.id === this.fitter?.brandId)
        ?.logo,
      'fitterBrandIdLogo'
    );
  }

  formatGoogleTime(time: string): string {
    return time.substring(0, 2) + ':' + time.substring(2);
  }

  groupOpeningPeriodsByDay(): Map<number, FitterOpeningPeriod[]> {
    const result = new Map<number, FitterOpeningPeriod[]>();
    for (const period of this.fitter?.openingHours?.center || []) {
      const day = period.open.day;
      if (result.has(day)) {
        result.get(day).push(period);
      } else {
        result.set(day, [period]);
      }
    }
    return result;
  }

  onShowOpeningPeriodsClick() {
    this.showOpeningsRedirect.emit();
  }

  onFitterItineraryButtonClick(): void {
    this.overlayService.showOverlay({
      type: 'itinerary',
      input: {
        fitter: this.fitter,
        userAddress: this.currentDossier?.searchCriteria?.address,
      },
    });
  }

  onShowMoreCommentsClick(): void {
    if (ScreenUtils.isMobileResolution()) {
      this.showCommentsRedirect.emit();
    } else {
      this.overlayService.showOverlay({
        type: 'fitter-comments',
        input: {
          fitter: this.fitter,
          itineraryDistanceInMeters: this.itineraryDistanceInMeters,
          itineraryDurationInSeconds: this.itineraryDurationInSeconds,
        },
      });
    }
  }

  onGoBack = () => {
    let obs: Observable<CTAQuerySetAction | null> = of(null);
    if (!this.currentDossier?.searchCriteria?.address) {
      const newSearchLoc = AppointmentUtils.getFitterLocation(this.fitter);
      obs = this.sandboxService
        .getPlacesFromAddress(
          `${newSearchLoc?.addressElements?.postalCode} ${newSearchLoc?.addressElements?.city} ${newSearchLoc?.addressElements?.country}`
        )
        .pipe(
          switchMap((places) => {
            if (places?.length) {
              return this.sandboxService.getGeoCodeFromPlaceId(
                places[0]?.placeId
              );
            } else {
              throw 'address not found';
            }
          }),
          take(1),
          catchError((err) => of(newSearchLoc)),
          switchMap((place) =>
            this.dossierSB.patchDossier({
              ...this.currentDossier,
              searchCriteria: {
                ...this.currentDossier?.searchCriteria,
                address: {
                  ...place,
                },
              },
            })
          ),
          switchMapTo(this.fittersSB.unselectFitter()),
          take(1)
        );
    }

    obs.subscribe({
      next: () => this.clickReturn.emit(this.isModificationMode()),
      error: () => this.clickReturn.emit(this.isModificationMode()),
    });
  };

  onClickChoseOtherFitterBtn() {
    this.clickChoseOtherFitter.emit();
  }

  onChangeItinerary() {
    this.updateItinerary.emit({
      meters: this.itineraryDistanceInMeters,
      seconds: this.itineraryDurationInSeconds,
    });
  }

  onSlotClick(clickedAppointment: Appointment) {
    this.selectedAppointment = clickedAppointment;

    if (
      this.featureFlippingService.isFeatureActivated(
        'tag-commander-global-buffer'
      )
    ) {
      this.tmService.pushTag({
        isPopIn: true,
        pageName: 'PopinCreneau',
      });
    }

    this.fittersSB.fitters$
      .pipe(
        take(1),
        map(
          (fitters) =>
            fitters.find((f) => f.id === this.selectedAppointment.fitterId) ||
            this.fitter
        ),
        switchMap((f) =>
          this.overlayService.showOverlay({
            type: 'appointment-validation',
            input: {
              displayReplacementVehicleSelection:
                this.featureFlippingService.isFeatureActivated(
                  'can-declare-replacement-vehicle-insurer'
                ) &&
                this.featureFlippingService.isBrandFeatureActivated(
                  this.fitter.brandId,
                  'can-declare-replacement-vehicle-fitter'
                ),
              appointment: clickedAppointment,
              location: this.getSearchLocationGeocoded(),
              customer: this.currentDossier?.customer,
              onlyReplacementVehicle:
                this.currentDossier?.searchCriteria?.filters?.interventionParams
                  ?.replacementVehicle,
              fitter: f,
            },
          })
        ),
        take(1),
        switchMap((outputData) => {
          if (outputData.type === 'cancel') {
            return EMPTY;
          }

          return of(outputData.data);
        }),
        switchMap((outputData) =>
          this.sandboxService
            .getItinerary(
              this.currentDossier?.searchCriteria?.address?.placeId,
              this.fitter?.location,
              dayjs(this.selectedAppointment.time).unix()
            )
            .pipe(map((itinerary) => [outputData, itinerary] as const))
        ),
        map(([outputData, itinerary]): [Dossier, boolean] => {
          let isModifMode = false;

          const previousAppointments = [
            ...(this.currentDossier?.previousAppointments || []),
          ];

          if (this.isModificationMode()) {
            if (this.currentDossier?.appointment) {
              previousAppointments.push({
                ...this.currentDossier?.appointment,
              });
            }
            isModifMode = true;
          }

          return [
            {
              ...this.currentDossier,
              customer: {
                ...outputData.customer,
              },
              appointment: {
                ...this.selectedAppointment,
                interventionLocation: outputData.location,
                isReplacementVehicleSelected: outputData.replacementVehicle,
                itinerary: itinerary,
              },
              previousAppointments: previousAppointments,
              searchCriteria: {
                ...this.currentDossier?.searchCriteria,
                address: this.selectedAppointment?.outsideIntervention
                  ? outputData.location
                  : this.currentDossier?.searchCriteria?.address,
              },
            },
            isModifMode,
          ];
        })
      )
      .subscribe(([result, isModifMode]) => {
        this.book.emit({ dossier: result, modified: isModifMode });
      });
  }

  onRequestAppointment() {
    this.overlayService
      .showOverlay({
        type: 'appointment-validation',
        input: {
          displayReplacementVehicleSelection:
            this.featureFlippingService.isFeatureActivated(
              'can-declare-replacement-vehicle-insurer'
            ) &&
            this.featureFlippingService.isBrandFeatureActivated(
              this.fitter.brandId,
              'can-declare-replacement-vehicle-fitter'
            ),
          onlyReplacementVehicle:
            this.currentDossier?.searchCriteria?.filters?.interventionParams
              ?.replacementVehicle,
          location: this.getSearchLocationGeocoded(),
          customer: this.currentDossier?.customer,
          fitter: this.fitter,
        },
      })
      .pipe(
        take(1),
        map((outputData): 'cancel' | Dossier => {
          if (outputData.type === 'success') {
            const bookedDossier: Dossier = {
              ...this.currentDossier,
              customer: {
                ...outputData.data.customer,
              },
              appointment: this.currentDossier?.appointment?.status
                ? { ...this.currentDossier?.appointment }
                : null,
              requestAppointment: {
                fitterId: this.fitter.id,
                brandId: this.fitter.brandId,
                timezone: this.fitter.timezone,
                outsideIntervention:
                  outputData.data.locationChoice === 'outside',
                interventionLocation: outputData.data.location,
                isReplacementVehicleSelected:
                  outputData.data.replacementVehicle,
              },
            };

            return bookedDossier;
          } else {
            return 'cancel';
          }
        }),
        take(1),
        tap(() => this.changeDetectorRef.detectChanges())
      )
      .subscribe((result) => {
        if (result !== 'cancel') {
          this.requestAppointment.emit({ dossier: result });
        }
      });
  }

  displayAppointmentRequest() {
    return this.sandboxService.canDoRequestAppointment(this.fitter?.brandId);
  }

  isFitterRequestOnlyToCustomer() {
    return this.featureFlippingService.sendRequestNotificationOnlyToCustomer(
      this.fitter?.brandId
    );
  }

  getOnlyCustomerKey() {
    return this.isFitterRequestOnlyToCustomer() ? 'onlyCustomer' : 'all';
  }

  getNoSlotDescription() {
    return `CustomerDeclaration.ConfirmAppointmentSheet.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey()}.description`;
  }

  getRequestAppointmentBtnText() {
    return `AppointmentNearestList.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey()}.apmtRequestBtn`;
  }
}
